import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import LinkMui5 from 'components/atoms/Link';
import NextLinkMui5 from 'components/atoms/NextLink';
import { StyledButtonMui5 } from 'components/atoms/StyledButton';
import ConfirmIdentityDialog, {
  IConfirmIdentityDialog,
  ViewMode,
} from 'containers/auth/Login/ConfirmIdentityDialog';
import MFADialog, { IConfirmMFA } from 'containers/auth/Login/MFADialog';
import { ConsumerSignupFormV2 } from 'containers/auth/Signup/ConsumerSignupFormV2';
import {
  EMAIL_CONFIRMATION_NOTE,
  EMAIL_CONFIRMATION_SPAN_NOTICE,
  EMAIL_CONFIRMATION_TITLE,
} from 'data/constants';
import { useSignup } from 'hooks/useSignup';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FC, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { signUpPopupOpenAtom, userAtom } from 'recoil/atoms';
import { BRAND_NAME, LOGO_TYPE, SITE_LOGO } from 'shared/utils/constants';
import { getSiteLogoStyleProps } from '../../utils/functions';

export const SignUpPopup: FC = () => {
  const [open, setOpen] = useRecoilState(signUpPopupOpenAtom);
  const [showSignUpForm, setShowSignUpForm] = useState(false);
  const user = useRecoilValue(userAtom);
  const router = useRouter();
  const handleSignup = useSignup();
  const mFADialog = useRef<IConfirmMFA>(null);
  const confirmIdentityDialogRef = useRef<IConfirmIdentityDialog>(null);

  const handleClose = () => {
    setOpen(false);
  };

  if (user || ['/login', '/signup'].includes(router.pathname)) {
    handleClose();
    return null;
  }

  return (
    <>
      <MFADialog ref={mFADialog} />
      <ConfirmIdentityDialog
        viewMode={ViewMode.EMAIL}
        ref={confirmIdentityDialogRef}
        title={EMAIL_CONFIRMATION_TITLE}
        text={EMAIL_CONFIRMATION_NOTE}
        secondaryText={EMAIL_CONFIRMATION_SPAN_NOTICE}
      />
      <Dialog
        open={open}
        onClose={() => {}}
        keepMounted={false}
        disableEscapeKeyDown={true}
        maxWidth={'xs'}
      >
        <DialogTitle textAlign={'center'}>
          <Image
            alt={`${BRAND_NAME} logo`}
            src={SITE_LOGO}
            {...getSiteLogoStyleProps(LOGO_TYPE)}
          />
        </DialogTitle>
        <DialogContent>
          {showSignUpForm ? (
            <Box my={1}>
              <ConsumerSignupFormV2
                showGetInTouch={false}
                onSignup={(user) => {
                  handleSignup({
                    user: {
                      email: user.email,
                      password: user.password,
                    },
                    refMFA: mFADialog,
                    refPhoneConfirmation: confirmIdentityDialogRef,
                    returnTo: router.asPath,
                  });
                  handleClose();
                }}
              />
            </Box>
          ) : (
            <>
              <Typography
                variant={'h5'}
                textAlign={'center'}
                style={{ maxWidth: '280px' }}
              >
                Sign up to unlock your full savings potential
              </Typography>
              <Box my={2}>
                <StyledButtonMui5
                  variant='contained'
                  size='medium'
                  fullWidth
                  className='js-push-signin-button'
                  onClick={() => {
                    setShowSignUpForm(true);
                  }}
                >
                  {'Sign Up'}
                </StyledButtonMui5>
              </Box>
              <Box my={2}>
                <Typography variant='body2'>
                  Already have an account?{' '}
                  <LinkMui5 component={NextLinkMui5} href='/login'>
                    Sign In
                  </LinkMui5>
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
